import { TotpRequest } from '@exam-prep/contracts';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@exam-prep/ui';
import { useState } from 'react';
import { Field, Input } from '../../../components/FormFields';
import { ErrorBox } from '../../../components/Feedback';
import { SubmitButton, ValidatedForm } from '../../../components/ValidatedForm';
import { ruleFor } from '../../../lib/validation/field-rule';
import { digitsOnly } from '../../../lib/validation/sanitize';

export function Shell({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 p-6">
      <div className="flex flex-col items-center gap-3 text-center">
        <img
          src="/brand/logo-192.png"
          alt=""
          width={112}
          height={112}
          className="brand-halo size-28"
        />
        <p className="font-display text-2xl font-bold">
          <span className="brand-accent-text">SSCExamPrep</span>
        </p>
        <p className="-mt-2 text-[11px] font-semibold tracking-[0.24em] text-muted-foreground uppercase">
          Admin portal
        </p>
      </div>
      <Card className="bg-[var(--brand-surface)] backdrop-blur-xl">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
      <p className="text-center text-xs text-muted-foreground">
        Authorised staff only. Sign-ins are logged and protected with two-factor authentication.
      </p>
    </main>
  );
}

const CODE_LENGTH = 6;
const codeRule = ruleFor(TotpRequest, 'code');

/**
 * The 6-digit authenticator code. Only digits can be typed (pasted spaces or dashes are dropped)
 * and the button stays disabled until all six are there.
 */
export function CodeForm({
  onSubmit,
  cta,
}: {
  onSubmit: (code: string) => Promise<void>;
  cta: string;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const submit = () => {
    setBusy(true);
    setError(null);
    onSubmit(code)
      .catch((err: unknown) => {
        setError(err);
        setCode('');
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <ValidatedForm className="flex flex-col gap-3" onValidSubmit={submit}>
      <Field label="6-digit code" hint="Digits only">
        <Input
          rule={codeRule}
          inputMode="numeric"
          // No native cap: it would cut a pasted "123 456" before the spaces are dropped.
          maxLength={undefined}
          autoComplete="one-time-code"
          className="h-12 text-center font-mono text-2xl tracking-[0.5em]"
          autoFocus
          value={code}
          onChange={(e) => {
            setCode(digitsOnly(e.target.value, CODE_LENGTH));
          }}
        />
      </Field>
      <ErrorBox error={error} />
      <SubmitButton
        disableUntilValid
        disabled={busy}
        pending={busy}
        pendingOverlay="Checking the code…"
      >
        {cta}
      </SubmitButton>
    </ValidatedForm>
  );
}
